import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Trash2, Download, ScanLine } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/Bits";
import { Field, Modal, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PatientPicker } from "@/components/PatientPicker";
import { useAuth } from "@/context/AuthContext";
import { dmy } from "@/lib/format";

const DOC_TYPES = [
  { v: "scan", l: "Scan" }, { v: "lab_report", l: "Lab Report" },
  { v: "prescription", l: "Prescription" }, { v: "discharge_summary", l: "Discharge Summary" },
  { v: "consent", l: "Consent Form" }, { v: "insurance", l: "Insurance" },
  { v: "id_proof", l: "ID Proof" }, { v: "radiology", l: "Radiology" },
  { v: "referral", l: "Referral" }, { v: "other", l: "Other" },
];

export default function PatientDocuments() {
  const { can } = useAuth();
  const canUpload = can("hospital.documents.upload");
  const canDelete = can("hospital.documents.delete");
  const [patient, setPatient] = useState(null);
  const [docs, setDocs] = useState(null);
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("scan");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", doc_type: "scan", category: "", note: "" });

  const load = useCallback(async () => {
    if (!patient) { setDocs(null); return; }
    setDocs(null);
    try { const { data } = await api.get("/hospital/documents", { params: { patient_id: patient.patient_id } }); setDocs(data.items); }
    catch (e) { toast.error(apiError(e)); }
  }, [patient]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file or scan to upload."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("patient_id", patient.patient_id);
      fd.append("title", title);
      fd.append("doc_type", docType);
      fd.append("category", category);
      fd.append("note", note);
      await api.post("/hospital/documents", fd);
      toast.success("Document uploaded.");
      setFile(null); setTitle(""); setCategory(""); setNote(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const startEdit = (document) => {
    setEditing(document);
    setEditForm({
      title: document.title || document.filename,
      doc_type: document.doc_type,
      category: document.category || "",
      note: document.note || "",
    });
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    try {
      await api.patch(`/hospital/documents/${editing.document_id}`, editForm);
      toast.success("Document details saved.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const view = async (d) => {
    try {
      const res = await api.get(`/hospital/documents/${d.document_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (d) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/hospital/documents/${d.document_id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!can("hospital.documents.view")) return <NoAccess what="Patient documents" />;

  return (
    <div data-testid="documents-page">
      <PageHeader overline="Records" title="Patient Documents & Scans"
        subtitle="Scan or upload patient/encounter documents — lab reports, prescriptions, discharge summaries, consent forms and ID proofs. Access is role-controlled and every view is audited." />

      <div className="panel p-5 mb-6">
        <p className="text-[12px] font-semibold uppercase tracking-wider text-slate-700 mb-3">Select patient</p>
        <PatientPicker selected={patient} onSelect={setPatient} />
      </div>

      {patient && canUpload && (
        <form onSubmit={upload} className="panel p-5 mb-6 space-y-4" data-testid="doc-upload-form">
          <p className="text-[12px] font-semibold uppercase tracking-wider text-slate-700">Add a document</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="File (PDF / JPG / PNG / WEBP · max 10 MB)">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" capture="environment"
                data-testid="doc-file-input" className={inputCls}
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </Field>
            <Field label="Document type">
              <select className={inputCls} data-testid="doc-type-select" value={docType} onChange={(e) => setDocType(e.target.value)}>
                {DOC_TYPES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </Field>
            <Field label="Document name / title *">
              <input required className={inputCls} data-testid="doc-title-input" value={title}
                placeholder="e.g. Previous prescription"
                onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label="Category / tag (optional)"><input className={inputCls} data-testid="doc-category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Cardiology" /></Field>
            <Field label="Note (optional)"><input className={inputCls} data-testid="doc-note" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          </div>
          <div className="flex items-center gap-3">
            <button disabled={busy} className={primaryBtnCls} data-testid="doc-upload-btn"><Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload"}</button>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><ScanLine className="h-4 w-4" /> On mobile, the file picker opens the camera to scan.</span>
          </div>
        </form>
      )}

      {!patient ? (
        <div className="panel"><EmptyState title="No patient selected" subtitle="Search and select a patient to view or add their documents." testid="doc-no-patient" /></div>
      ) : docs === null ? <Loading /> : docs.length === 0 ? (
        <div className="panel"><EmptyState title="No documents yet" subtitle="Scan or upload the first document for this patient." testid="doc-empty" /></div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="doc-list">
          {docs.map((d) => {
            const isImg = (d.content_type || "").startsWith("image/");
            return (
              <div key={d.document_id} className="panel p-4" data-testid={`doc-${d.document_id}`}>
                <div className="flex items-start gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-500">
                    {isImg ? <ImageIcon className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate" data-testid={`doc-title-${d.document_id}`}>
                      {d.title || d.filename}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 capitalize">{String(d.doc_type).replace(/_/g, " ")}{d.category ? ` · ${d.category}` : ""}</p>
                    {d.encounter_type && <p className="text-[11px] text-slate-400 mt-0.5">{d.encounter_type.toUpperCase()}{d.encounter_id ? ` · ${d.encounter_id}` : ""}</p>}
                    {d.note && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{d.note}</p>}
                    <p className="text-[11px] text-slate-400 mt-1" data-testid={`doc-saved-date-${d.document_id}`}>
                      Saved {dmy(d.uploaded_at)} · {d.uploaded_by_name}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => view(d)} className={ghostBtnCls} data-testid={`doc-view-${d.document_id}`}><Download className="h-4 w-4" /> Open</button>
                  {canUpload && <button onClick={() => startEdit(d)} className={ghostBtnCls}
                    data-testid={`doc-edit-${d.document_id}`}>Edit</button>}
                  {canDelete && <button onClick={() => remove(d)} className={ghostBtnCls} data-testid={`doc-delete-${d.document_id}`}><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Modal open={Boolean(editing)} onClose={() => setEditing(null)} title="Edit document"
        subtitle="Document name and type are kept separate." testid="edit-document-modal">
        <form onSubmit={saveEdit} className="space-y-4">
          <Field label="Document name / title *">
            <input required className={inputCls} data-testid="edit-doc-title-input" value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
          </Field>
          <Field label="Document type">
            <select className={inputCls} data-testid="edit-doc-type-select" value={editForm.doc_type}
              onChange={(e) => setEditForm({ ...editForm, doc_type: e.target.value })}>
              {DOC_TYPES.map((option) => <option key={option.v} value={option.v}>{option.l}</option>)}
            </select>
          </Field>
          <Field label="Category / tag"><input className={inputCls} value={editForm.category}
            onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} /></Field>
          <Field label="Note"><input className={inputCls} value={editForm.note}
            onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} /></Field>
          <button className={primaryBtnCls} data-testid="save-document-edit-btn">Save changes</button>
        </form>
      </Modal>
    </div>
  );
}
